import Controller, { inject as controller } from '@ember/controller';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import { endOfDay, formatISO, startOfDay } from 'date-fns';
import type AbilitiesService from 'ember-can/services/abilities';
import { Changeset } from 'ember-changeset';
import type { DetailedChangeset } from 'ember-changeset/types';
import lookupValidator from 'ember-changeset-validations';
import { dropTask } from 'ember-concurrency';
import type AgreementModel from 'garaje/models/agreement';
import type CustomUserDocumentTemplateModel from 'garaje/models/custom-user-document-template';
import type DocumentUploadPageModel from 'garaje/models/document-upload-page';
import type Flow from 'garaje/models/flow';
import type InviteModel from 'garaje/models/invite';
import type CurrentLocationService from 'garaje/services/current-location';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type { TransitionSource } from 'garaje/services/global-routing';
import type GlobalRoutingService from 'garaje/services/global-routing';
import type LocationFeatureFlagsService from 'garaje/services/location-feature-flags';
import { isSensitiveFieldMasked } from 'garaje/services/sensitive-fields-visibility';
import type SensitiveFieldsVisibilityService from 'garaje/services/sensitive-fields-visibility';
import type StateService from 'garaje/services/state';
import { modifyDateInTimeZone } from 'garaje/utils/date-fns-tz-utilities';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import { createCapacityValidator } from 'garaje/utils/locations-capacity';
import { resolveReturnToBackLink } from 'garaje/utils/return-to-back-link';
import buildInviteValidations from 'garaje/validations/invite';
import { reads } from 'macro-decorators';
import moment from 'moment-timezone';
import { defer } from 'rsvp';

import type VisitorsInvitesIndexController from '../index/controller';
import type { FlowsTaskResponse } from '../route';

import type { VisitorsInvitesShowRouteModel } from './route';

export default class VisitorsInvitesShowController extends Controller {
  declare model: VisitorsInvitesShowRouteModel;

  queryParams = ['transitionedFrom', 'returnTo', 'returnToModel', 'returnToTab'];

  @service declare store: StoreService;
  @service declare abilities: AbilitiesService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare locationFeatureFlags: LocationFeatureFlagsService;
  @service declare router: RouterService;
  @service declare state: StateService;
  @service declare globalRouting: GlobalRoutingService;
  @service declare flashMessages: FlashMessagesService;
  @service declare currentLocation: CurrentLocationService;
  @service declare sensitiveFieldsVisibility: SensitiveFieldsVisibilityService;

  @tracked changeset!: DetailedChangeset<InviteModel>;

  // The guest-name field's display name keys both `sensitiveFields` and the
  // shared reveal state; the form's eyeball toggles it and the header/monogram
  // below read it, so all three reveal together.
  get nameFieldKey(): string | undefined {
    return this.model?.signInFields?.find((field) => field.isGuestName)?.name;
  }

  get isNameMasked(): boolean {
    const key = this.nameFieldKey;
    if (!key) return false;
    return isSensitiveFieldMasked(
      this.model.invite.sensitiveFields?.[key],
      this.sensitiveFieldsVisibility.isRevealed(key),
    );
  }

  @tracked transitionedFrom: TransitionSource = 'location';

  // Optional origin threaded by callers (e.g. the visitor-profile Screeners
  // tab "View" button) so Back returns to where the user came from instead of
  // the invites log. `returnTo` presence switches the back target.
  @tracked returnTo: string | null = null;
  @tracked returnToModel: string | null = null;
  @tracked returnToTab: string | null = null;

  get hasAttachments(): boolean {
    return (this.model?.invite?.visitorDocuments?.slice() ?? []).some(
      (doc) => doc.hasAttachedFile || doc.hasInputFieldData,
    );
  }

  @controller('visitors.invites.index') pregistered!: VisitorsInvitesIndexController; // pregistered and date are used to build the navigation header
  @reads('state.currentLocation.timezone', 'America/Los_Angeles') timezone!: string;
  @reads('flowDataResult.flows') flows?: Flow[];

  setupChangeset(): void {
    const { store, model } = this;

    const validations = buildInviteValidations({
      validateCapacity: createCapacityValidator(store, model.location),
      checkEmailRequired:
        this.featureFlags.isEnabled('visitors-required-email-field') ||
        this.locationFeatureFlags.isEnabled('visitors-required-email-field-by-location'),
      store: this.store,
    });
    const validator = lookupValidator(validations);

    this.changeset = Changeset(model.invite, validator, validations);
  }

  get backLink(): { route: string; model?: string; query?: Record<string, string> } {
    const resolved = resolveReturnToBackLink(this.returnTo, this.returnToModel);
    if (resolved) {
      return {
        route: resolved.route,
        model: resolved.models[0],
        query: this.returnToTab ? { tab: this.returnToTab } : undefined,
      };
    }
    return { route: this.globalRouting.getBackLink(this.transitionedFrom) };
  }

  get flowDataResult(): FlowsTaskResponse | undefined {
    const result = this.model.loadFlowDataTask?.lastSuccessful?.value;
    if (result?.locationId !== this.currentLocation.location.id) return undefined;
    return result;
  }

  get hasAnyUserDocumentTemplateConfigurations(): boolean {
    return (this.flows ?? []).some((f) => !!f.activeUserDocumentTemplateConfigurations.length);
  }

  get requiredDocumentTemplates(): CustomUserDocumentTemplateModel[] {
    if (!this.abilities.can('access document-upload')) return [];
    const invite = this.model?.invite;
    if (!invite) return [];
    // flow is AsyncBelongsTo — use .value() to get the synchronously resolved model
    const flow = invite.belongsTo('flow').value() as Flow | null;
    if (!flow) return [];
    const page = flow.belongsTo('documentUploadPage').value() as DocumentUploadPageModel | null;
    if (!page?.enabled) return [];
    return page.customUserDocumentTemplates?.slice() ?? [];
  }

  /** Availability AND authorization: a reviewer, or the host of this invite. Availability alone never grants it. */
  get canManageDocuments(): boolean {
    if (!this.abilities.can('manage admin-pre-reg-document')) return false;

    return (
      this.abilities.can('review visitor-document') || this.abilities.can('upload-documents invite', this.model?.invite)
    );
  }

  /**
   * Review authority alone. Pairing it with the availability gate would withhold Approve/Deny from a
   * reviewer whenever management is switched off, even though the tab still lists pending documents.
   * Remove takes its availability from manage mode, which already requires the gate.
   */
  get canReviewDocuments(): boolean {
    return this.abilities.can('review visitor-document');
  }

  @action
  reloadDocuments(): void {
    // surfaceErrors: this is the post-write manage reload — a silent failure would leave the admin
    // looking at stale documents with no signal (mirrors the entry controller). The initial load
    // still swallows (see loadVisitorDocumentsTask).
    void this.loadVisitorDocumentsTask.perform({ surfaceErrors: true });
    void this.reloadVisitApprovalTask.perform();
  }

  reloadVisitApprovalTask = dropTask(async (): Promise<void> => {
    try {
      await this.store.findRecord('invite', this.model.invite.id, {
        reload: true,
        include: 'visit-approval,visit-approval.screener-results,visit-approval.screener-reviews',
      });
    } catch (e) {
      // Fired fire-and-forget after a doc write; a failed approval refresh is non-critical (the doc
      // reload surfaces its own error), so swallow rather than leave an unhandled rejection — log so
      // it isn't fully silent.
      // eslint-disable-next-line no-console
      console.error('[invites/show] reloadVisitApproval failed', e);
    }
  });

  @action
  async deletePhoto(): Promise<void> {
    try {
      this.model.invite.deletePhoto = true;
      await this.model.invite.save();

      this.flashMessages.showAndHideFlash('success', 'Visitor photo deleted');
    } catch (_error) {
      this.flashMessages.showFlash('error', 'Unable to delete visitor photo', { details: 'Please try again' });
    }
  }

  get legalDocumentDescriptions(): Record<string, string> {
    if (!this.shouldShowLegalDocumentDescriptions) {
      return {};
    }

    /**
     * This grabs all of the agreements for which the invite has signed agreeableNdas that fall in the following
     * two categories:
     * 1. Signed agreements that are on an agreementPage that is different from the invite's currently assigned
     *    flow's agreementPage. These represent agreements that may have been signed during pre-registration,
     *    and later the flow was disabled or deleted or switched, or the agreementPage was disabled.
     * 2. Signed agreements on the currently assigned flow's agreementPage that are disabled. We exclude enabled
     *    agreements on the current agreementPage here because we concat these in the next reduce function below.
     *    We include discarded signed agreements in the invite payload so these should be available in the store.
     */
    const previouslySignedAgreements = this.model.invite.agreeableNdas.reduce<AgreementModel[]>(
      (array, agreeableNda) => {
        const isOnAnotherAgreementPage = this.model.agreements?.every(({ id }) => id !== agreeableNda.agreement.id);
        const isDisabled = agreeableNda.agreement.enabled === false;

        if (agreeableNda.signedAt && (isOnAnotherAgreementPage || isDisabled)) {
          return [...array, agreeableNda.agreement];
        }

        return array;
      },
      [],
    );

    return previouslySignedAgreements
      .concat((this.model.agreements ?? []).filter((agreement) => agreement.enabled))
      .reduce<Record<string, string>>((obj, agreement) => {
        const agreeableNda = this.model.invite.agreeableNdas.find((nda) => nda.agreement.id === agreement.id);
        /**
         * There may be agreeableNdas that have signed agreements (i.e. an "NDA" PDF) but that don't have a
         * relationship to an agreement (i.e., a legal document template) and therefore have no agreement name.
         * These signed agreements are from before visitor type flows existed. We assign them a generic
         * "Non-disclosure Agreement" name because NDAs used to be the only kind of agreement we supported.
         */
        const agreementName = typeof agreement.name === 'undefined' ? 'Non-disclosure Agreement' : agreement.name;

        if (agreeableNda && agreeableNda.signedAt) {
          const guestUpdatedAt = moment(this.model.invite.guestUpdatedAt);
          const agreementSignedAt = moment(agreeableNda.signedAt);
          const wasPreviouslySigned = guestUpdatedAt.diff(agreementSignedAt, 'days') !== 0;
          const description = wasPreviouslySigned ? `signed ${agreementSignedAt.format('MMM D, YYYY')}` : 'signed';

          obj[agreementName] = description;
        } else {
          obj[agreementName] = 'pending';
        }

        return obj;
      }, {});
  }

  get shouldShowLegalDocumentDescriptions(): boolean {
    return (
      this.model.invite.agreeableNdas.any((agreeableNda) => !!agreeableNda.signedAt) ||
      !!this.model.agreements?.any((agreement) => agreement.enabled) ||
      !!this.model.invite.ndaDiscarded
    );
  }

  get locationIsConnectedToProperty(): boolean {
    return this.model.connectedTenants.slice().length > 0;
  }

  @action
  modelDidSave(date?: Date): void {
    if (this.model.invite.needsApprovalReview) {
      return;
    }

    const queryParams: { startDate?: string; endDate?: string } = {};

    if (date) {
      queryParams.startDate = formatISO(modifyDateInTimeZone(date, this.timezone, startOfDay));
      queryParams.endDate = formatISO(modifyDateInTimeZone(date, this.timezone, endOfDay));
    }

    void this.router.transitionTo(this.globalRouting.getBackLink(this.transitionedFrom, 'visitors.invites.index'), {
      queryParams,
    });
  }

  get modalElement(): HTMLElement | null {
    return document.getElementById('modal');
  }

  @dropTask
  showConfirmationModalTask = {
    *perform(this: { abort?: () => void; continue?: () => void }): Generator<Promise<unknown>, unknown, unknown> {
      const deferred = defer();

      this.abort = () => deferred.reject();
      this.continue = () => deferred.resolve();

      return yield deferred.promise;
    },
  };

  loadVisitorDocumentsTask = dropTask(
    async ({ surfaceErrors = false }: { surfaceErrors?: boolean } = {}): Promise<void> => {
      // Fetch when the prereg document-upload feature is available OR when legacy
      // user-document-template configurations exist (pre-feature behavior that predates
      // the Chargebee gate — preserved so legacy customers keep seeing their documents).
      const isDocUploadsEnabled = this.abilities.can('access document-upload');
      if (!isDocUploadsEnabled && !this.hasAnyUserDocumentTemplateConfigurations) {
        return;
      }
      try {
        // Include user-document-links only when document uploads feature is on,
        // to sideload approval status for the Documents review tab.
        const include = isDocUploadsEnabled
          ? 'user-document-template,user-document-links,user-document-attachments,user-document-attachments.user-document-template-attachment'
          : 'user-document-template,user-document-attachments,user-document-attachments.user-document-template-attachment';
        const visitorDocuments = await this.store.query('visitor-document', {
          include,
          filter: { 'invite-id': this.model.invite.id },
        });

        this.model.invite.visitorDocuments = visitorDocuments;
        this.model.invite.visitorDocumentsLoaded = true;
      } catch (e) {
        // Initial load swallows: UserDocumentTemplateConfigurations load for everyone until
        // [BE] https://envoycom.atlassian.net/browse/VIS-4390 is done, so a visitor-document fetch
        // failure must not bomb the page. The post-write manage reload passes surfaceErrors so a
        // failed reload is visible instead of leaving stale documents on screen.
        if (surfaceErrors) {
          this.flashMessages.showFlash('error', 'Failed to reload documents', parseErrorForDisplay(e));
        }
      }
    },
  );
}
